// file: Report.jsx

import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  IconButton,
  Tooltip,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import FinancialReportPrint from "./FinancialReportPrint";

/* =====================================================
   ProfessionalTable Component
   A reusable table with subtle styling, 
   a left border color bar, and minimal clutter.
===================================================== */
const ProfessionalTable = ({ title, rows, columns, onRowClick }) => (
  <Paper
    elevation={4}
    sx={{
      p: 3,
      mb: 3,
      borderRadius: 8,
      borderLeft: "6px solid rgb(11, 122, 170)", // Purple accent
      backgroundColor: "#ffffff",
      maxWidth: "1000px",
      margin: "0 auto",
      overflow: "hidden",
    }}
  >
    {title && (
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 2,
          color: "rgb(11, 122, 170)",
          textAlign: "left",
        }}
      >
        {title}
      </Typography>
    )}
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 4,
        overflowX: "auto",
        boxShadow: "none",
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "rgb(11, 122, 170)" }}>
            {columns.map((col, index) => (
              <TableCell
                key={index}
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "0.9rem",
                  whiteSpace: "nowrap",
                  color: "#fff",
                  border: "none",
                }}
              >
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                "&:nth-of-type(even)": { backgroundColor: "#FAF5FF" },
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: onRowClick ? "#F3E5F5" : "inherit",
                  cursor: onRowClick ? "pointer" : "default",
                },
              }}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col, colIndex) => (
                <TableCell
                  key={colIndex}
                  sx={{
                    color: row[col] ? "#333333" : "#999999",
                    fontSize: "0.9rem",
                    borderBottom: "1px solid #EDE7F6",
                  }}
                >
                  {row[col] ?? "N/A"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

/* =====================================================
   DetailModal Component
   Fetches transaction-level data from e.g.
   finance-report/details/<sectionId>/ 
   and displays it in a table with a PDF download button 
   for just that data.
===================================================== */
const DetailModal = ({
  open,
  onClose,
  sectionId,
  sectionTitle,
  token,
  startDate,
  endDate,
}) => {
  // Build the URL with optional date range
  let detailUrl = `financial-reports/details/${sectionId}/`;
  const params = [];
  if (startDate)
    params.push(`start_date=${startDate.toISOString().split("T")[0]}`);
  if (endDate) params.push(`end_date=${endDate.toISOString().split("T")[0]}`);
  if (params.length > 0) {
    detailUrl += `?${params.join("&")}`;
  }

  const { data, isLoading, isError, error } = useFetchObjects(
    ["financial-detail", sectionId, startDate, endDate],
    detailUrl,
    token
  );

  // We'll capture the table ref to export only that table
  const tableRef = useRef(null);

  // Convert fetched data into rows
  const rows = useMemo(() => {
    if (!data) return [];
    // The Python 'details' action returns an array of objects
    // with keys like ["id", "name", "status", "amount", "date"].
    return data;
  }, [data]);

  // Download PDF for just this table
  const handleDownloadTablePDF = async () => {
    if (!tableRef.current) return;
    const tableElement = tableRef.current.querySelector(".detail-table");
    if (!tableElement) return;

    const doc = new jsPDF("p", "pt", "a4");
    const scale = 2;

    // Convert the table to canvas
    const canvas = await html2canvas(tableElement, {
      scale,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");

    const imgWidth = 595.28; // A4 page width in pts
    const pageHeight = 841.89;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    doc.setFontSize(12);
    doc.text(`${sectionTitle} - Detailed Transactions`, 40, 30);

    doc.addImage(imgData, "PNG", 0, 50, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight + 50;
      doc.addPage();
      doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    doc.save(`${sectionTitle}_Details.pdf`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "12px", overflow: "hidden" },
      }}
    >
      <DialogTitle sx={{ bgcolor: "rgb(11, 122, 170)", color: "#fff" }}>
        {sectionTitle} - Detailed Transactions
      </DialogTitle>

      <DialogContent ref={tableRef} dividers sx={{ bgcolor: "#FAF5FF" }}>
        {isLoading ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4 }}
          >
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error.message}
          </Alert>
        ) : rows.length > 0 ? (
          <div className="detail-table">
            <ProfessionalTable
              title=""
              rows={rows}
              columns={["id", "name", "status", "amount", "date"]}
            />
          </div>
        ) : (
          <Typography variant="body1" sx={{ mt: 2 }}>
            No detailed transactions found.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: "#F3E5F5" }}>
        <Box flex="1" textAlign="left" ml={2}>
          {rows.length > 0 && (
            <Tooltip title="Download PDF of this table">
              <IconButton
                onClick={handleDownloadTablePDF}
                sx={{
                  backgroundColor: "rgb(11, 122, 170)",
                  color: "#fff",
                  mr: 2,
                  "&:hover": {
                    backgroundColor: "#4A148C",
                  },
                }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: "rgb(11, 122, 170)",
            "&:hover": { backgroundColor: "#4A148C" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* =====================================================
   FilterBar Component
   Provides date range pickers, 
   a filter button, 
   and top-level PDF/Print controls.
===================================================== */
const FilterBar = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onFilter,
  onDownloadPDF,
  onPrint,
  reportType,
  setReportType,
}) => (
  <Paper
    sx={{
      p: 2,
      borderRadius: 4,
      backgroundColor: "#fff",
    }}
    elevation={3}
  >
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <DatePicker
          selected={startDate}
          onChange={setStartDate}
          dateFormat="yyyy-MM-dd"
          placeholderText="Start Date"
          className="date-picker-input"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DatePicker
          selected={endDate}
          onChange={setEndDate}
          dateFormat="yyyy-MM-dd"
          placeholderText="End Date"
          className="date-picker-input"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Button
          fullWidth
          variant="contained"
          onClick={onFilter}
          sx={{
            height: "100%",
            backgroundColor: "rgb(11, 122, 170)",
            "&:hover": { backgroundColor: "#4A148C" },
            fontWeight: "bold",
          }}
        >
          Apply Filter
        </Button>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="Download Full PDF">
            <IconButton
              onClick={onDownloadPDF}
              sx={{
                backgroundColor: "rgb(11, 122, 170)",
                color: "#fff",
                "&:hover": { backgroundColor: "#4A148C" },
              }}
            >
              <PictureAsPdfIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Print Full Report">
            <IconButton
              onClick={onPrint}
              sx={{
                backgroundColor: "rgb(11, 122, 170)",
                color: "#fff",
                "&:hover": { backgroundColor: "#4A148C" },
              }}
            >
              <PrintIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Grid>
      <Grid item xs={12}></Grid>
    </Grid>
  </Paper>
);

/* =====================================================
   ReportContent Component
   - Renders each section's table or chart 
   - "View Details" button triggers the DetailModal
===================================================== */
const ReportContent = ({ sections, reportRef, onViewDetails }) => (
  <Box ref={reportRef} sx={{ mt: 4 }}>
    <Grid container spacing={3}>
      {sections.map((section) => {
        const rows = Object.entries(section.data || {}).map(
          ([label, value]) => ({
            label,
            value: value ?? "N/A",
          })
        );

        return (
          <Grid item xs={12} md={6} key={section.id}>
            <ProfessionalTable
              title={section.title}
              rows={rows}
              columns={["label", "value"]}
            />
            <Box textAlign="right" mb={2}>
              <Button
                variant="contained"
                onClick={() => onViewDetails(section)}
                sx={{
                  backgroundColor: "rgb(11, 122, 170)",
                  "&:hover": { backgroundColor: "#4A148C" },
                  fontWeight: "bold",
                }}
              >
                View Details
              </Button>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  </Box>
);

/* =====================================================
   Main Report Component (FinanceReport)
   - Fetches summary data
   - Allows date filter
   - Renders multi-section overview
   - Prints / downloads entire report
===================================================== */
const Report = () => {
  const auth = useAuth();
  const token = auth?.user?.token;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [fetchParams, setFetchParams] = useState(
    "financial-reports/"
  );

  const { data, isLoading, isError, error } = useFetchObjects(
    ["financial-report", fetchParams],
    fetchParams,
    token
  );

  const [reportType, setReportType] = useState("table");

  // For the detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [currentDetailSection, setCurrentDetailSection] = useState(null);

  // Reference for exporting the entire page as PDF
  const reportRef = useRef();
  const printRef = useRef();

  // Filter
  const handleFilter = () => {
    const params = new URLSearchParams();
    if (startDate)
      params.append("start_date", startDate.toISOString().split("T")[0]);
    if (endDate) params.append("end_date", endDate.toISOString().split("T")[0]);

    const newUrl = `financial-reports/?${params.toString()}`;
    setFetchParams(newUrl);
  };

  const allSections = useMemo(() => {
    return [
      {
        id: "payroll",
        title: "Payroll and Expenses",
        data: data?.payroll_and_expenses || {},
      },
      {
        id: "medicine_sales",
        title: "Medicine Sales",
        data: data?.medicine_sales || {},
      },
      {
        id: "prescription_sales",
        title: "Prescription Sales",
        data: data?.prescription || {},
      },
      {
        id: "purchase",
        title: "Purchase",
        data: {
          total_purchase_expenses: data?.purchase?.total_purchase_expenses,
          unapproved_purchase_expenses:
            data?.purchase?.unapproved_purchase_expenses,
        },
      },
      {
        id: "summary",
        title: "Summary",
        data: data?.summary || {},
      },
    ];
  }, [data]);

  // Optional: let user "select" which sections to show
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);

  const sectionsToRender =
    selectedSectionIds.length === 0
      ? allSections
      : allSections.filter((sec) => selectedSectionIds.includes(sec.id));

  // Download entire page as PDF
  const downloadPDF = () => {
    const doc = new jsPDF("p", "pt", "a4");
    doc.setFontSize(14);
    doc.text("Financial Report", 40, 30);

    html2canvas(reportRef.current, { scale: 2, useCORS: true }).then(
      (canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 595.28;
        const pageHeight = 841.89;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 50; // move image down a bit for the title

        doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight + 50;
          doc.addPage();
          doc.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
        doc.save("Financial_Report.pdf");
      }
    );
  };

  // Print entire page
  const handlePrintClick = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printRef.current.innerHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Open detail modal
  const handleViewDetails = (section) => {
    setCurrentDetailSection(section);
    setDetailModalOpen(true);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#F3E5F5",
        p: 3,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          color: "#4A148C",
          mb: 2,
          textAlign: "center",
        }}
      >
        Financial Report
      </Typography>

      {/* Filter Bar */}
      <Box sx={{ width: "100%" }}>
        <FilterBar
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          onFilter={handleFilter}
          onDownloadPDF={downloadPDF}
          onPrint={handlePrintClick}
          reportType={reportType}
          setReportType={setReportType}
        />
      </Box>

      {/* Section Selector (clickable cards) */}
      <Grid container spacing={2} mt={3}>
        {allSections.map((section) => {
          const isSelected = selectedSectionIds.includes(section.id);
          return (
            <Grid item xs={12} sm={6} md={3} key={section.id}>
              <Paper
                onClick={() =>
                  setSelectedSectionIds((prev) =>
                    prev.includes(section.id)
                      ? prev.filter((id) => id !== section.id)
                      : [...prev, section.id]
                  )
                }
                elevation={3}
                sx={{
                  cursor: "pointer",
                  p: 2,
                  border: isSelected
                    ? "2px solid rgb(11, 122, 170)"
                    : "2px solid transparent",
                  bgcolor: isSelected ? "#FCE4EC" : "#fff",
                  "&:hover": { bgcolor: "#F8BBD0" },
                  transition: "all 0.3s ease",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: isSelected ? "bold" : "normal",
                    color: isSelected ? "rgb(11, 122, 170)" : "#4A148C",
                  }}
                >
                  {section.title}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Main Content (Summary tables or graphs) */}
      <Box ref={reportRef}>
        {isLoading ? (
          <Stack direction="row" justifyContent="center" mt={3}>
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error.message}
          </Alert>
        ) : (
          <ReportContent
            sections={sectionsToRender}
            reportRef={reportRef}
            onViewDetails={handleViewDetails}
          />
        )}
      </Box>

      {/* Hidden print section */}
      <div ref={printRef} style={{ display: "none" }}>
        <FinancialReportPrint data={data} sections={allSections} />
      </div>

      {/* Detail Modal */}
      {currentDetailSection && (
        <DetailModal
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          sectionId={currentDetailSection.id}
          sectionTitle={currentDetailSection.title}
          token={token}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </Box>
  );
};

export default Report;
